import { Row } from 'components/Layout';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { UnrealisedCashFXGainOrLossTypeLabels } from 'further-ui/labels';
import { numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import { useState } from 'react';
import CashHistory from './CashHistory';
import getUnrealisedFxDisplayAmount from './getUnrealisedFxDisplayAmount';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { styled } from '@mui/material/styles';
import { Column } from 'further-ui/components/Layout';
import usePostUnrealisedFxGainsData from './usePostUnrealisedFxGainsData';

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  onViewRecord?: (recordId: string) => void;
};

const ConfirmedCash: React.FC<Props> = ({ onViewRecord }) => {
  const { vehicle } = useLayoutContext();
  const {
    confirmedCashData,
    confirmedCashLoading,
    confirmedCashPagination,
    periodId,
  } = usePostUnrealisedFxGainsData();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  if (!vehicle) {
    return null;
  }

  const confirmedRows = confirmedCashData?.results ?? [];

  const confirmedColumns: Array<TableColumn<(typeof confirmedRows)[number]>> = [
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate?.toLabel?.() ?? '-',
    },
    {
      key: 'bankAccountName',
      label: 'Bank Account',
      render: (row) => row.bankAccountName ?? '-',
      disableSorting: true,
    },
    {
      key: 'gainOrLossType',
      label: 'Type',
      render: (row) =>
        UnrealisedCashFXGainOrLossTypeLabels[row.gainOrLossType] ?? '-',
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) =>
        numberToCurrencyString(
          getUnrealisedFxDisplayAmount(row.gainOrLossType, row.amountLcy),
          {
            currency: vehicle.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      disableSorting: true,
    },
  ];

  return (
    <Column spacing="lg">
      <Table
        columns={confirmedColumns}
        rows={confirmedRows}
        isLoading={confirmedCashLoading}
        pagination={confirmedCashPagination}
        rowsTotalCount={confirmedCashData?.totalCount ?? 0}
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRowId === row.id}
        onRowExpand={(row) =>
          setExpandedRowId((prev) => (prev === row.id ? null : row.id))
        }
        getExpandedContent={(row) => (
          <ExpandedRowContainer>
            <Row fullWidth>
              {!transactionTypesLoading && (
                <TransactionAllocator
                  readOnly
                  transactionTypeOptions={transactionTypeOptions}
                  transactions={row.transactions}
                  transactionCurrency={row.currencyCode}
                  vehicle={vehicle}
                  fxRate={row.currencyRate ?? null}
                />
              )}
            </Row>
          </ExpandedRowContainer>
        )}
      />
      <CashHistory
        vehicleId={vehicle._id}
        periodId={periodId}
        onViewRecord={onViewRecord}
      />
    </Column>
  );
};

export default ConfirmedCash;
